import { createFeatureSelector, createSelector } from '@ngrx/store'
import { UndoRedoState } from './undo-redo.models'
import {
  PRESENTER_WIDGETS_FEATURE_KEY,
  PresenterWidgetsState,
  initialPresenterWidgetsState,
  countdownWidgetsAdapter,
  imageWidgetsAdapter,
  textWidgetsAdapter,
  smartTextWidgetsAdapter,
  totalRaisedWidgetsAdapter,
  teamScoringWidgetsAdapter,
  powerPledgeWidgetsAdapter,
  notificationWidgetsAdapter,
  itemTableWidgetsAdapter,
  raffleWinnersWidgetsAdapter,
  honourRollWidgetsAdapter,
  thermometerWidgetsAdapter,
  raffleDrawWidgetsAdapter,
  qrCodeWidgetsAdapter,
  videoWidgetsAdapter,
} from './presenter-widgets.reducer'
import { WidgetModel } from './presenter-widgets.models'

// Lookup the 'PresenterWidgets' feature state managed by NgRx
export const selectPresenterWidgetsState =
  createFeatureSelector<UndoRedoState<PresenterWidgetsState>>(PRESENTER_WIDGETS_FEATURE_KEY)

const initialUndoRedoState: UndoRedoState<PresenterWidgetsState> = {
  past: [],
  present: initialPresenterWidgetsState,
  future: [],
}

export const selectPresenterWidgetsPresentState = createSelector(
  selectPresenterWidgetsState,
  (state) => (state ?? initialUndoRedoState).present,
)

export const selectPresenterWidgetsPastStates = createSelector(
  selectPresenterWidgetsState,
  (state) => (state ?? initialUndoRedoState).past,
)

export const selectPresenterWidgetsFutureStates = createSelector(
  selectPresenterWidgetsState,
  (state) => (state ?? initialUndoRedoState).future,
)

export const selectPresenterWidgetsPreviousPresentState = createSelector(selectPresenterWidgetsPastStates, (past) =>
  past.length ? past[past.length - 1] : undefined,
)

export const selectPresenterWidgetsNextPresentState = createSelector(selectPresenterWidgetsFutureStates, (future) =>
  future.length ? future[0] : undefined,
)

export const selectCanUndo = createSelector(selectPresenterWidgetsPastStates, (past) => past.length > 0)

export const selectCanRedo = createSelector(selectPresenterWidgetsFutureStates, (future) => future.length > 0)

// Selected Widget Selectors (across all widget types)
export const selectSelectedWidget = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.selectedWidget,
)

export const selectSelectedWidgetType = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.selectedWidgetType,
)

export const selectSavingWidget = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.savingWidget,
)

// Countdown Widgets Selectors
export const selectCountdownWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.countdownWidgets,
)

const { selectAll: selectAllCountdownWidgets, selectEntities: selectCountdownWidgetsEntities } =
  countdownWidgetsAdapter.getSelectors()

export const selectCountdownWidgetsLoaded = createSelector(selectCountdownWidgetsState, (state) => state.loaded)

export const selectCountdownWidgetsError = createSelector(selectCountdownWidgetsState, (state) => state.error)

export const selectAllCountdownWidgetsList = createSelector(selectCountdownWidgetsState, (state) =>
  selectAllCountdownWidgets(state),
)

export const selectCountdownWidgetsEntitiesMap = createSelector(selectCountdownWidgetsState, (state) =>
  selectCountdownWidgetsEntities(state),
)

export const selectSelectedCountdownWidgetId = createSelector(selectCountdownWidgetsState, (state) => state.selectedId)

export const selectSelectedCountdownWidget = createSelector(
  selectCountdownWidgetsEntitiesMap,
  selectSelectedCountdownWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectCountdownWidgetsEditCreateLoaded = createSelector(
  selectCountdownWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectCountdownWidgetsCreateLoaded = createSelector(
  selectCountdownWidgetsState,
  (state) => state.createLoaded,
)

// Image Widgets Selectors
export const selectImageWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.imageWidgets,
)

const { selectAll: selectAllImageWidgets, selectEntities: selectImageWidgetsEntities } =
  imageWidgetsAdapter.getSelectors()

export const selectImageWidgetsLoaded = createSelector(selectImageWidgetsState, (state) => state.loaded)

export const selectImageWidgetsError = createSelector(selectImageWidgetsState, (state) => state.error)

export const selectAllImageWidgetsList = createSelector(selectImageWidgetsState, (state) =>
  selectAllImageWidgets(state),
)

export const selectImageWidgetsEntitiesMap = createSelector(selectImageWidgetsState, (state) =>
  selectImageWidgetsEntities(state),
)

export const selectSelectedImageWidgetId = createSelector(selectImageWidgetsState, (state) => state.selectedId)

export const selectSelectedImageWidget = createSelector(
  selectImageWidgetsEntitiesMap,
  selectSelectedImageWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectImageWidgetsEditCreateLoaded = createSelector(
  selectImageWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectImageWidgetsCreateLoaded = createSelector(selectImageWidgetsState, (state) => state.createLoaded)

// Text Widgets Selectors
export const selectTextWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.textWidgets,
)

const { selectAll: selectAllTextWidgets, selectEntities: selectTextWidgetsEntities } = textWidgetsAdapter.getSelectors()

export const selectTextWidgetsLoaded = createSelector(selectTextWidgetsState, (state) => state.loaded)

export const selectTextWidgetsError = createSelector(selectTextWidgetsState, (state) => state.error)

export const selectAllTextWidgetsList = createSelector(selectTextWidgetsState, (state) => selectAllTextWidgets(state))

export const selectTextWidgetsEntitiesMap = createSelector(selectTextWidgetsState, (state) =>
  selectTextWidgetsEntities(state),
)

export const selectSelectedTextWidgetId = createSelector(selectTextWidgetsState, (state) => state.selectedId)

export const selectSelectedTextWidget = createSelector(
  selectTextWidgetsEntitiesMap,
  selectSelectedTextWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectTextWidgetsEditCreateLoaded = createSelector(
  selectTextWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectTextWidgetsCreateLoaded = createSelector(selectTextWidgetsState, (state) => state.createLoaded)

// Smart Text Widgets Selectors
export const selectSmartTextWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.smartTextWidgets,
)

const { selectAll: selectAllSmartTextWidgets, selectEntities: selectSmartTextWidgetsEntities } =
  smartTextWidgetsAdapter.getSelectors()

export const selectSmartTextWidgetsLoaded = createSelector(selectSmartTextWidgetsState, (state) => state.loaded)

export const selectSmartTextWidgetsError = createSelector(selectSmartTextWidgetsState, (state) => state.error)

export const selectAllSmartTextWidgetsList = createSelector(selectSmartTextWidgetsState, (state) =>
  selectAllSmartTextWidgets(state),
)

export const selectSmartTextWidgetsEntitiesMap = createSelector(selectSmartTextWidgetsState, (state) =>
  selectSmartTextWidgetsEntities(state),
)

export const selectSelectedSmartTextWidgetId = createSelector(selectSmartTextWidgetsState, (state) => state.selectedId)

export const selectSelectedSmartTextWidget = createSelector(
  selectSmartTextWidgetsEntitiesMap,
  selectSelectedSmartTextWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectSmartTextWidgetsEditCreateLoaded = createSelector(
  selectSmartTextWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectSmartTextWidgetsCreateLoaded = createSelector(
  selectSmartTextWidgetsState,
  (state) => state.createLoaded,
)

// Total Raised Widgets Selectors
export const selectTotalRaisedWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.totalRaisedWidgets,
)

const { selectAll: selectAllTotalRaisedWidgets, selectEntities: selectTotalRaisedWidgetsEntities } =
  totalRaisedWidgetsAdapter.getSelectors()

export const selectTotalRaisedWidgetsLoaded = createSelector(selectTotalRaisedWidgetsState, (state) => state.loaded)

export const selectTotalRaisedWidgetsError = createSelector(selectTotalRaisedWidgetsState, (state) => state.error)

export const selectAllTotalRaisedWidgetsList = createSelector(selectTotalRaisedWidgetsState, (state) =>
  selectAllTotalRaisedWidgets(state),
)

export const selectTotalRaisedWidgetsEntitiesMap = createSelector(selectTotalRaisedWidgetsState, (state) =>
  selectTotalRaisedWidgetsEntities(state),
)

export const selectSelectedTotalRaisedWidgetId = createSelector(
  selectTotalRaisedWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedTotalRaisedWidget = createSelector(
  selectTotalRaisedWidgetsEntitiesMap,
  selectSelectedTotalRaisedWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectTotalRaisedWidgetsEditCreateLoaded = createSelector(
  selectTotalRaisedWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectTotalRaisedWidgetsCreateLoaded = createSelector(
  selectTotalRaisedWidgetsState,
  (state) => state.createLoaded,
)

// Team Scoring Widgets Selectors
export const selectTeamScoringWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.teamScoringWidgets,
)

const { selectAll: selectAllTeamScoringWidgets, selectEntities: selectTeamScoringWidgetsEntities } =
  teamScoringWidgetsAdapter.getSelectors()

export const selectTeamScoringWidgetsLoaded = createSelector(selectTeamScoringWidgetsState, (state) => state.loaded)

export const selectTeamScoringWidgetsError = createSelector(selectTeamScoringWidgetsState, (state) => state.error)

export const selectAllTeamScoringWidgetsList = createSelector(selectTeamScoringWidgetsState, (state) =>
  selectAllTeamScoringWidgets(state),
)

export const selectTeamScoringWidgetsEntitiesMap = createSelector(selectTeamScoringWidgetsState, (state) =>
  selectTeamScoringWidgetsEntities(state),
)

export const selectSelectedTeamScoringWidgetId = createSelector(
  selectTeamScoringWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedTeamScoringWidget = createSelector(
  selectTeamScoringWidgetsEntitiesMap,
  selectSelectedTeamScoringWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectTeamScoringWidgetsEditCreateLoaded = createSelector(
  selectTeamScoringWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectTeamScoringWidgetsCreateLoaded = createSelector(
  selectTeamScoringWidgetsState,
  (state) => state.createLoaded,
)

// Power Pledge Widgets Selectors
export const selectPowerPledgeWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.powerPledgeWidgets,
)

const { selectAll: selectAllPowerPledgeWidgets, selectEntities: selectPowerPledgeWidgetsEntities } =
  powerPledgeWidgetsAdapter.getSelectors()

export const selectPowerPledgeWidgetsLoaded = createSelector(selectPowerPledgeWidgetsState, (state) => state.loaded)

export const selectPowerPledgeWidgetsError = createSelector(selectPowerPledgeWidgetsState, (state) => state.error)

export const selectAllPowerPledgeWidgetsList = createSelector(selectPowerPledgeWidgetsState, (state) =>
  selectAllPowerPledgeWidgets(state),
)

export const selectPowerPledgeWidgetsEntitiesMap = createSelector(selectPowerPledgeWidgetsState, (state) =>
  selectPowerPledgeWidgetsEntities(state),
)

export const selectSelectedPowerPledgeWidgetId = createSelector(
  selectPowerPledgeWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedPowerPledgeWidget = createSelector(
  selectPowerPledgeWidgetsEntitiesMap,
  selectSelectedPowerPledgeWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectPowerPledgeWidgetsEditCreateLoaded = createSelector(
  selectPowerPledgeWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectPowerPledgeWidgetsCreateLoaded = createSelector(
  selectPowerPledgeWidgetsState,
  (state) => state.createLoaded,
)

// Notification Widgets Selectors
export const selectNotificationWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.notificationWidgets,
)

const { selectAll: selectAllNotificationWidgets, selectEntities: selectNotificationWidgetsEntities } =
  notificationWidgetsAdapter.getSelectors()

export const selectNotificationWidgetsLoaded = createSelector(selectNotificationWidgetsState, (state) => state.loaded)

export const selectNotificationWidgetsError = createSelector(selectNotificationWidgetsState, (state) => state.error)

export const selectAllNotificationWidgetsList = createSelector(selectNotificationWidgetsState, (state) =>
  selectAllNotificationWidgets(state),
)

export const selectNotificationWidgetsEntitiesMap = createSelector(selectNotificationWidgetsState, (state) =>
  selectNotificationWidgetsEntities(state),
)

export const selectSelectedNotificationWidgetId = createSelector(
  selectNotificationWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedNotificationWidget = createSelector(
  selectNotificationWidgetsEntitiesMap,
  selectSelectedNotificationWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectNotificationWidgetsEditCreateLoaded = createSelector(
  selectNotificationWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectNotificationWidgetsCreateLoaded = createSelector(
  selectNotificationWidgetsState,
  (state) => state.createLoaded,
)

// Item Table Widgets Selectors
export const selectItemTableWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.itemTableWidgets,
)

const { selectAll: selectAllItemTableWidgets, selectEntities: selectItemTableWidgetsEntities } =
  itemTableWidgetsAdapter.getSelectors()

export const selectItemTableWidgetsLoaded = createSelector(selectItemTableWidgetsState, (state) => state.loaded)

export const selectItemTableWidgetsError = createSelector(selectItemTableWidgetsState, (state) => state.error)

export const selectAllItemTableWidgetsList = createSelector(selectItemTableWidgetsState, (state) =>
  selectAllItemTableWidgets(state),
)

export const selectItemTableWidgetsEntitiesMap = createSelector(selectItemTableWidgetsState, (state) =>
  selectItemTableWidgetsEntities(state),
)

export const selectSelectedItemTableWidgetId = createSelector(selectItemTableWidgetsState, (state) => state.selectedId)

export const selectSelectedItemTableWidget = createSelector(
  selectItemTableWidgetsEntitiesMap,
  selectSelectedItemTableWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectItemTableWidgetsEditCreateLoaded = createSelector(
  selectItemTableWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectItemTableWidgetsCreateLoaded = createSelector(
  selectItemTableWidgetsState,
  (state) => state.createLoaded,
)

// Raffle Winners Widgets Selectors
export const selectRaffleWinnersWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.raffleWinnersWidgets,
)

const { selectAll: selectAllRaffleWinnersWidgets, selectEntities: selectRaffleWinnersWidgetsEntities } =
  raffleWinnersWidgetsAdapter.getSelectors()

export const selectRaffleWinnersWidgetsLoaded = createSelector(selectRaffleWinnersWidgetsState, (state) => state.loaded)

export const selectRaffleWinnersWidgetsError = createSelector(selectRaffleWinnersWidgetsState, (state) => state.error)

export const selectAllRaffleWinnersWidgetsList = createSelector(selectRaffleWinnersWidgetsState, (state) =>
  selectAllRaffleWinnersWidgets(state),
)

export const selectRaffleWinnersWidgetsEntitiesMap = createSelector(selectRaffleWinnersWidgetsState, (state) =>
  selectRaffleWinnersWidgetsEntities(state),
)

export const selectSelectedRaffleWinnersWidgetId = createSelector(
  selectRaffleWinnersWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedRaffleWinnersWidget = createSelector(
  selectRaffleWinnersWidgetsEntitiesMap,
  selectSelectedRaffleWinnersWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectRaffleWinnersWidgetsEditCreateLoaded = createSelector(
  selectRaffleWinnersWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectRaffleWinnersWidgetsCreateLoaded = createSelector(
  selectRaffleWinnersWidgetsState,
  (state) => state.createLoaded,
)

// Honour Roll Widgets Selectors
export const selectHonourRollWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.honourRollWidgets,
)

const { selectAll: selectAllHonourRollWidgets, selectEntities: selectHonourRollWidgetsEntities } =
  honourRollWidgetsAdapter.getSelectors()

export const selectHonourRollWidgetsLoaded = createSelector(selectHonourRollWidgetsState, (state) => state.loaded)

export const selectHonourRollWidgetsError = createSelector(selectHonourRollWidgetsState, (state) => state.error)

export const selectAllHonourRollWidgetsList = createSelector(selectHonourRollWidgetsState, (state) =>
  selectAllHonourRollWidgets(state),
)

export const selectHonourRollWidgetsEntitiesMap = createSelector(selectHonourRollWidgetsState, (state) =>
  selectHonourRollWidgetsEntities(state),
)

export const selectSelectedHonourRollWidgetId = createSelector(
  selectHonourRollWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedHonourRollWidget = createSelector(
  selectHonourRollWidgetsEntitiesMap,
  selectSelectedHonourRollWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectHonourRollWidgetsEditCreateLoaded = createSelector(
  selectHonourRollWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectHonourRollWidgetsCreateLoaded = createSelector(
  selectHonourRollWidgetsState,
  (state) => state.createLoaded,
)

// Thermometer Widgets Selectors
export const selectThermometerWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.thermometerWidgets,
)

const { selectAll: selectAllThermometerWidgets, selectEntities: selectThermometerWidgetsEntities } =
  thermometerWidgetsAdapter.getSelectors()

export const selectThermometerWidgetsLoaded = createSelector(selectThermometerWidgetsState, (state) => state.loaded)

export const selectThermometerWidgetsError = createSelector(selectThermometerWidgetsState, (state) => state.error)

export const selectAllThermometerWidgetsList = createSelector(selectThermometerWidgetsState, (state) =>
  selectAllThermometerWidgets(state),
)

export const selectThermometerWidgetsEntitiesMap = createSelector(selectThermometerWidgetsState, (state) =>
  selectThermometerWidgetsEntities(state),
)

export const selectSelectedThermometerWidgetId = createSelector(
  selectThermometerWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedThermometerWidget = createSelector(
  selectThermometerWidgetsEntitiesMap,
  selectSelectedThermometerWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectThermometerWidgetsEditCreateLoaded = createSelector(
  selectThermometerWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectThermometerWidgetsCreateLoaded = createSelector(
  selectThermometerWidgetsState,
  (state) => state.createLoaded,
)

// Raffle Draw Widgets Selectors
export const selectRaffleDrawWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.raffleDrawWidgets,
)

const { selectAll: selectAllRaffleDrawWidgets, selectEntities: selectRaffleDrawWidgetsEntities } =
  raffleDrawWidgetsAdapter.getSelectors()

export const selectRaffleDrawWidgetsLoaded = createSelector(selectRaffleDrawWidgetsState, (state) => state.loaded)

export const selectRaffleDrawWidgetsError = createSelector(selectRaffleDrawWidgetsState, (state) => state.error)

export const selectAllRaffleDrawWidgetsList = createSelector(selectRaffleDrawWidgetsState, (state) =>
  selectAllRaffleDrawWidgets(state),
)

export const selectRaffleDrawWidgetsEntitiesMap = createSelector(selectRaffleDrawWidgetsState, (state) =>
  selectRaffleDrawWidgetsEntities(state),
)

export const selectSelectedRaffleDrawWidgetId = createSelector(
  selectRaffleDrawWidgetsState,
  (state) => state.selectedId,
)

export const selectSelectedRaffleDrawWidget = createSelector(
  selectRaffleDrawWidgetsEntitiesMap,
  selectSelectedRaffleDrawWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectRaffleDrawWidgetsEditCreateLoaded = createSelector(
  selectRaffleDrawWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectRaffleDrawWidgetsCreateLoaded = createSelector(
  selectRaffleDrawWidgetsState,
  (state) => state.createLoaded,
)

// QR Code Widgets Selectors
export const selectQrCodeWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.qrCodeWidgets,
)

const { selectAll: selectAllQrCodeWidgets, selectEntities: selectQrCodeWidgetsEntities } =
  qrCodeWidgetsAdapter.getSelectors()

export const selectQrCodeWidgetsLoaded = createSelector(selectQrCodeWidgetsState, (state) => state.loaded)

export const selectQrCodeWidgetsError = createSelector(selectQrCodeWidgetsState, (state) => state.error)

export const selectAllQrCodeWidgetsList = createSelector(selectQrCodeWidgetsState, (state) =>
  selectAllQrCodeWidgets(state),
)

export const selectQrCodeWidgetsEntitiesMap = createSelector(selectQrCodeWidgetsState, (state) =>
  selectQrCodeWidgetsEntities(state),
)

export const selectSelectedQrCodeWidgetId = createSelector(selectQrCodeWidgetsState, (state) => state.selectedId)

export const selectSelectedQrCodeWidget = createSelector(
  selectQrCodeWidgetsEntitiesMap,
  selectSelectedQrCodeWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectQrCodeWidgetsEditCreateLoaded = createSelector(
  selectQrCodeWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectQrCodeWidgetsCreateLoaded = createSelector(selectQrCodeWidgetsState, (state) => state.createLoaded)

// Video Widgets Selectors
export const selectVideoWidgetsState = createSelector(
  selectPresenterWidgetsPresentState,
  (state: PresenterWidgetsState) => state.videoWidgets,
)

const { selectAll: selectAllVideoWidgets, selectEntities: selectVideoWidgetsEntities } =
  videoWidgetsAdapter.getSelectors()

export const selectVideoWidgetsLoaded = createSelector(selectVideoWidgetsState, (state) => state.loaded)

export const selectVideoWidgetsError = createSelector(selectVideoWidgetsState, (state) => state.error)

export const selectAllVideoWidgetsList = createSelector(selectVideoWidgetsState, (state) =>
  selectAllVideoWidgets(state),
)

export const selectVideoWidgetsEntitiesMap = createSelector(selectVideoWidgetsState, (state) =>
  selectVideoWidgetsEntities(state),
)

export const selectSelectedVideoWidgetId = createSelector(selectVideoWidgetsState, (state) => state.selectedId)

export const selectSelectedVideoWidget = createSelector(
  selectVideoWidgetsEntitiesMap,
  selectSelectedVideoWidgetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectVideoWidgetsEditCreateLoaded = createSelector(
  selectVideoWidgetsState,
  (state) => state.editCreateLoaded,
)

export const selectVideoWidgetsCreateLoaded = createSelector(selectVideoWidgetsState, (state) => state.createLoaded)

export const selectAllWidgets = createSelector(
  selectAllCountdownWidgetsList,
  selectAllImageWidgetsList,
  selectAllTextWidgetsList,
  selectAllSmartTextWidgetsList,
  selectAllTotalRaisedWidgetsList,
  selectAllTeamScoringWidgetsList,
  selectAllPowerPledgeWidgetsList,
  selectAllNotificationWidgetsList,
  selectAllItemTableWidgetsList,
  selectAllRaffleWinnersWidgetsList,
  selectAllHonourRollWidgetsList,
  selectAllThermometerWidgetsList,
  selectAllRaffleDrawWidgetsList,
  selectAllQrCodeWidgetsList,
  selectAllVideoWidgetsList,
  (...lists: WidgetModel[][]): WidgetModel[] => lists.flat(),
)
